import React, { useState, useEffect } from 'react';
import { Ticket, Plus, Edit, Trash2, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_discount: '',
    per_user_limit: '1',
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/coupons');
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const handleAddCoupon = async () => {
    if (!newCoupon.code || !newCoupon.discount_value || !newCoupon.valid_from || !newCoupon.valid_until) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post('/coupons', {
        ...newCoupon,
        discount_value: parseFloat(newCoupon.discount_value),
        min_order_amount: newCoupon.min_order_amount ? parseFloat(newCoupon.min_order_amount) : 0,
        max_discount: newCoupon.max_discount ? parseFloat(newCoupon.max_discount) : null,
        per_user_limit: parseInt(newCoupon.per_user_limit) || 1,
        valid_from: new Date(newCoupon.valid_from).toISOString(),
        valid_until: new Date(newCoupon.valid_until).toISOString()
      });
      
      toast.success('Coupon created successfully!');
      setShowAdd(false);
      setNewCoupon({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        min_order_amount: '',
        max_discount: '',
        per_user_limit: '1',
        valid_from: '',
        valid_until: ''
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleToggleActive = async (couponId, currentActive) => {
    try {
      await api.put(`/coupons/${couponId}`, { active: !currentActive });
      toast.success(`Coupon ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      await api.delete(`/coupons/${couponId}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
            Coupon Management
          </h2>
          <p className="text-gray-400 text-sm">Create and manage discount coupons</p>
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
          data-testid="add-coupon-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-gray-400 text-sm">Total Coupons</p>
            <p className="text-2xl font-bold text-white">{coupons.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-gray-400 text-sm">Active Coupons</p>
            <p className="text-2xl font-bold text-green-400">
              {coupons.filter(c => c.active && !isExpired(c.valid_until)).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-gray-400 text-sm">Expired</p>
            <p className="text-2xl font-bold text-red-400">
              {coupons.filter(c => isExpired(c.valid_until)).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Coupons List */}
      <div className="space-y-4">
        {coupons.length === 0 ? (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="py-12 text-center">
              <Ticket className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No coupons created yet</p>
              <p className="text-gray-500 text-sm">Create your first discount coupon</p>
            </CardContent>
          </Card>
        ) : (
          coupons.map(coupon => (
            <Card 
              key={coupon.id} 
              className={`bg-[#121212] border-white/10 ${
                !coupon.active || isExpired(coupon.valid_until) ? 'opacity-60' : ''
              }`}
              data-testid={`coupon-${coupon.code}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="bg-[#FF4500]/20 border border-dashed border-[#FF4500] rounded px-3 py-1 cursor-pointer flex items-center gap-2"
                        onClick={() => copyCode(coupon.code)}
                      >
                        <span className="font-mono font-bold text-[#FF4500]">{coupon.code}</span>
                        {copiedCode === coupon.code ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      {!coupon.active ? (
                        <Badge className="bg-gray-500/20 text-gray-400">Inactive</Badge>
                      ) : isExpired(coupon.valid_until) ? (
                        <Badge className="bg-red-500/20 text-red-400">Expired</Badge>
                      ) : (
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      )}
                    </div>
                    
                    <p className="text-white mb-2">{coupon.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Discount: </span>
                        <span className="text-[#00E5FF] font-bold">
                          {coupon.discount_type === 'percentage' 
                            ? `${coupon.discount_value}%` 
                            : formatCurrency(coupon.discount_value)
                          }
                          {coupon.max_discount && coupon.discount_type === 'percentage' && 
                            ` (max ${formatCurrency(coupon.max_discount)})`
                          }
                        </span>
                      </div>
                      {coupon.min_order_amount > 0 && (
                        <div>
                          <span className="text-gray-500">Min Order: </span>
                          <span className="text-white">{formatCurrency(coupon.min_order_amount)}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Uses/User: </span>
                        <span className="text-white">{coupon.per_user_limit}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-500 text-xs mt-2">
                      Valid: {formatDate(coupon.valid_from)} - {formatDate(coupon.valid_until)}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={coupon.active ? "outline" : "default"}
                      onClick={() => handleToggleActive(coupon.id, coupon.active)}
                      className={coupon.active 
                        ? "border-white/20 text-white hover:bg-white/5" 
                        : "bg-green-500 hover:bg-green-600 text-white"
                      }
                    >
                      {coupon.active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteCoupon(coupon.id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Coupon Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>
              Create New Coupon
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2 block">Coupon Code *</Label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                className="bg-white/5 border-white/10 text-white font-mono"
                placeholder="SUMMER20"
                data-testid="coupon-code-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Description *</Label>
              <Input
                value={newCoupon.description}
                onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="20% off on summer items"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Discount Type *</Label>
                <Select 
                  value={newCoupon.discount_type} 
                  onValueChange={(v) => setNewCoupon({...newCoupon, discount_type: v})}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white mb-2 block">
                  {newCoupon.discount_type === 'percentage' ? 'Discount %' : 'Discount ₹'} *
                </Label>
                <Input
                  type="number"
                  value={newCoupon.discount_value}
                  onChange={(e) => setNewCoupon({...newCoupon, discount_value: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder={newCoupon.discount_type === 'percentage' ? '20' : '50'}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Min Order Amount</Label>
                <Input
                  type="number"
                  value={newCoupon.min_order_amount}
                  onChange={(e) => setNewCoupon({...newCoupon, min_order_amount: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="200"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Max Discount (₹)</Label>
                <Input
                  type="number"
                  value={newCoupon.max_discount}
                  onChange={(e) => setNewCoupon({...newCoupon, max_discount: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Uses per User</Label>
              <Input
                type="number"
                value={newCoupon.per_user_limit}
                onChange={(e) => setNewCoupon({...newCoupon, per_user_limit: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Valid From *</Label>
                <Input
                  type="datetime-local"
                  value={newCoupon.valid_from}
                  onChange={(e) => setNewCoupon({...newCoupon, valid_from: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Valid Until *</Label>
                <Input
                  type="datetime-local"
                  value={newCoupon.valid_until}
                  onChange={(e) => setNewCoupon({...newCoupon, valid_until: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
            <Button
              onClick={handleAddCoupon}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold"
              data-testid="create-coupon-submit"
            >
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponManagement;
